import {
  ComponentRef,
  Directive,
  ElementRef,
  HostListener,
  Inject,
  Input,
  OnInit,
  Renderer2,
  ViewContainerRef,
} from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { WINDOW } from '@ng-web-apis/common';

import { DynamicInputPlaceholderComponent } from '@prlw/libs/dynamic-input-placeholder/dynamic-input-placeholder.component';
import pluralize from '@prlw/libs/pluralize/pluralize';

const DEFAULT_FONT = '14px / 20px "TT Interphases Trl", sans';

@Directive({
  selector: '[prlwDynamicInputPlaceholder]',
})
export class DynamicInputPlaceholderDirective implements OnInit {
  @Input('prlwDynamicInputPlaceholder') public text: string = '';
  @Input() public defaultValue: string = '';
  @Input() public pluralizePlaceholder: string[] = [];
  private _wrapperComponent?: ComponentRef<DynamicInputPlaceholderComponent>;
  private readonly _canvas = this.document.createElement('canvas');

  constructor(
    @Inject(DOCUMENT) private readonly document: Document,
    @Inject(WINDOW) private readonly window: Window,
    private readonly el: ElementRef,
    private readonly renderer: Renderer2,
    private readonly viewContainerRef: ViewContainerRef,
  ) {}

  public ngOnInit(): void {
    if (this.el.nativeElement.nodeName !== 'INPUT') {
      throw new Error('Dynamic placeholder element must be an input!');
    }
    if (typeof this.defaultValue !== 'string') {
      throw new Error('Dynamic placeholder default value must be string!');
    }
    this._wrapperComponent = this.viewContainerRef.createComponent(
      DynamicInputPlaceholderComponent,
    );
    this._wrapperComponent.instance.text = this.text;
    const inputParent = this.el.nativeElement.parentElement;
    const wrapperElement = this._wrapperComponent.location.nativeElement;
    this.renderer.appendChild(
      wrapperElement.querySelector('.wrapper'),
      this.el.nativeElement,
    );
    this.renderer.appendChild(inputParent, wrapperElement);
    if (this.defaultValue) {
      this._updatePlaceholder(this.defaultValue);
    }
  }

  @HostListener('ngModelChange', ['$event'])
  public onNgModelChange(value: string | number | null): void {
    const formattedValue = !value && value !== 0 ? '' : String(value);
    this._updatePlaceholder(formattedValue);
  }

  private _updatePlaceholder(value: string): void {
    if (this._wrapperComponent) {
      const padding = this._getElementLeftPadding(this.el.nativeElement);
      const textWidth = this._getTextWidth(value);
      this._wrapperComponent.instance.margin = padding + textWidth;
      this._wrapperComponent.instance.isVisible = Boolean(value.length);
      if (this.pluralizePlaceholder.length) {
        this._wrapperComponent.instance.text = pluralize(
          Number(value),
          this.pluralizePlaceholder,
        );
      }
    }
  }

  private _getTextWidth(text: string): number {
    const context = this._canvas.getContext('2d');
    if (context) {
      context.font = this._getElementFont(this.el.nativeElement);
    }
    return context?.measureText(text)?.width ?? 0;
  }

  private _getElementFont(element: HTMLElement): string {
    return (
      this.window.getComputedStyle(element, null).getPropertyValue('font') ||
      DEFAULT_FONT
    );
  }

  private _getElementLeftPadding(element: HTMLElement): number {
    return Number(
      this.window
        .getComputedStyle(element, null)
        .getPropertyValue('padding-left')
        .replace('px', ''),
    );
  }
}
